/** Componentes de interface compartilhados (identidade visual do doc 08/09). */
import { useEffect, useId, type ReactNode } from 'react';

export function Aviso({ tipo = 'erro', children }: { tipo?: 'erro' | 'sucesso' | 'info'; children: ReactNode }) {
  if (!children) return null;
  return (
    <div className={`aviso aviso-${tipo}`} role={tipo === 'erro' ? 'alert' : 'status'}>
      {children}
    </div>
  );
}

export function Selo({ tom, children }: { tom: 'ativo' | 'inativo' | 'destaque' | 'neutro' | 'alerta'; children: ReactNode }) {
  return <span className={`selo selo-${tom}`}>{children}</span>;
}

export function SeloStatus({ ativo }: { ativo: boolean }) {
  return <Selo tom={ativo ? 'ativo' : 'inativo'}>{ativo ? 'Ativo' : 'Inativo'}</Selo>;
}

export function Campo({
  rotulo,
  children,
  ajuda,
  dica,
}: {
  rotulo: string;
  children: (id: string) => ReactNode;
  ajuda?: string;
  /** explicação secundária no "?" ao lado do rótulo (em vez de texto permanente) */
  dica?: ReactNode;
}) {
  const id = useId();
  return (
    <div className="campo">
      <label htmlFor={id}>
        {rotulo}
        {dica}
      </label>
      {children(id)}
      {ajuda && <small className="ajuda">{ajuda}</small>}
    </div>
  );
}

export function Modal({
  titulo,
  aoFechar,
  children,
  rodape,
}: {
  titulo: string;
  aoFechar: () => void;
  children: ReactNode;
  rodape?: ReactNode;
}) {
  useEffect(() => {
    const tecla = (e: KeyboardEvent) => e.key === 'Escape' && aoFechar();
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [aoFechar]);
  return (
    <div className="modal-fundo" onMouseDown={(e) => e.target === e.currentTarget && aoFechar()}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={titulo}>
        <header>
          <h2>{titulo}</h2>
          <button type="button" className="botao-icone" aria-label="Fechar" onClick={aoFechar}>
            ×
          </button>
        </header>
        <div className="modal-corpo">{children}</div>
        {rodape && <footer>{rodape}</footer>}
      </div>
    </div>
  );
}

/** Confirmação de ação crítica 🔒 (doc 09 — convenções transversais), com justificativa registrada na auditoria. */
export function ConfirmarAcao({
  titulo,
  descricao,
  rotuloConfirmar,
  aoConfirmar,
  aoFechar,
  exigirJustificativa = false,
  erro,
}: {
  titulo: string;
  descricao: ReactNode;
  rotuloConfirmar: string;
  aoConfirmar: (justificativa: string) => Promise<unknown>;
  aoFechar: () => void;
  exigirJustificativa?: boolean;
  erro?: string | null;
}) {
  return (
    <Modal titulo={titulo} aoFechar={aoFechar}>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          const j = String(new FormData(e.currentTarget).get('justificativa') ?? '');
          await aoConfirmar(j);
        }}
      >
        <Aviso>{erro}</Aviso>
        <p>{descricao}</p>
        <Campo rotulo={exigirJustificativa ? 'Justificativa (obrigatória)' : 'Justificativa (registrada na auditoria)'}>
          {(id) => <textarea id={id} name="justificativa" rows={3} required={exigirJustificativa} maxLength={1000} />}
        </Campo>
        <div className="acoes-form">
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="submit" className="botao-primario">
            {rotuloConfirmar}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function Vazio({ children }: { children: ReactNode }) {
  return <p className="vazio">{children}</p>;
}
